import { useEffect, useLayoutEffect } from 'react';
import { createRoot } from 'react-dom/client';
import './styles.css';
import './tailwind.css';
import './works-heymessage.css';
import './animated-card.css';
import './landing.css';
import { LandingFooter } from '@/components/landing/LandingFooter';
import { LandingNav } from '@/components/landing/LandingNav';
import { CinematicHero } from '@/components/ui/cinematic-landing-hero';
import { Gallery4, type Gallery4Item } from '@/components/ui/gallery4';
import { mountLandingWorksSplit } from './works.js';
import { initWorksAnimations, revealShowcaseContent } from './works-section-animations.js';

function shouldReduceMotion() {
  const isMobile = window.matchMedia('(max-width: 768px)').matches;
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = Boolean((navigator as any)?.connection?.saveData);
  return isMobile || prefersReduced || saveData;
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Missing #root for cinematic landing');
}

function LandingApp() {
  useEffect(() => {
    if (shouldReduceMotion()) {
      initWorksAnimations();
      const showcase = document.getElementById('works-landing-showcase');
      if (showcase) revealShowcaseContent(showcase);
      return;
    }

    let tickerFn: ((t: number) => void) | null = null;
    let lenis: any = null;
    let ScrollTrigger: any = null;
    let gsap: any = null;

    let cancelled = false;
    (async () => {
      const [{ default: Lenis }, gsapMod, stMod] = await Promise.all([
        import('@studio-freight/lenis'),
        import('gsap'),
        import('gsap/ScrollTrigger'),
      ]);
      if (cancelled) return;

      gsap = (gsapMod as any).default ?? gsapMod;
      ScrollTrigger = (stMod as any).ScrollTrigger ?? (stMod as any).default ?? stMod;
      gsap.registerPlugin(ScrollTrigger);

      lenis = new Lenis({
        duration: 0.62,
        easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 1.15,
        smoothTouch: false,
        touchMultiplier: 2.2,
        infinite: false,
      });

      (window as any).lenis = lenis;

      lenis.on('scroll', ScrollTrigger.update);
      tickerFn = (time: number) => lenis.raf(time * 1000);
      gsap.ticker.add(tickerFn);
      gsap.ticker.lagSmoothing(0);

      requestAnimationFrame(() => ScrollTrigger.refresh());

      // Run after Lenis is wired so ScrollTrigger measurements stay in sync.
      initWorksAnimations();
      requestAnimationFrame(() => ScrollTrigger.refresh());
      setTimeout(() => {
        ScrollTrigger.refresh();
        const showcase = document.getElementById('works-landing-showcase');
        if (showcase) revealShowcaseContent(showcase);
      }, 400);
    })().catch(() => {});

    return () => {
      cancelled = true;
      try {
        if (tickerFn && gsap) gsap.ticker.remove(tickerFn);
        if (lenis) lenis.destroy();
        delete (window as any).lenis;
        if (ScrollTrigger) ScrollTrigger.refresh();
      } catch {}
    };
  }, []);

  useEffect(() => {
    const yearEl = document.getElementById('footer-year');
    if (yearEl) yearEl.textContent = String(new Date().getFullYear());
  }, []);

  useLayoutEffect(() => {
    const forceVisible = (el: HTMLElement | null) => {
      if (!el) return;
      el.style.opacity = '1';
      el.style.visibility = 'visible';
      el.style.transform = 'none';
    };
    const needsFix = (el: HTMLElement | null) => {
      if (!el) return false;
      const cs = window.getComputedStyle(el);
      return cs.visibility === 'hidden' || Number(cs.opacity) === 0;
    };
    const runHeroFailsafe = (whatEl: HTMLElement) => {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (isMobile) {
        forceVisible(whatEl.querySelector<HTMLElement>('.works-usecase-heading'));
        forceVisible(whatEl.querySelector<HTMLElement>('.works-usecase-title'));
        whatEl.querySelectorAll<HTMLElement>('.animated-card').forEach((card) => forceVisible(card));
        return;
      }
      setTimeout(() => {
        const heading = whatEl.querySelector<HTMLElement>('.works-usecase-heading');
        const title = whatEl.querySelector<HTMLElement>('.works-usecase-title');
        if (needsFix(heading)) forceVisible(heading);
        if (needsFix(title)) forceVisible(title);
        whatEl.querySelectorAll<HTMLElement>('.animated-card').forEach((card) => {
          if (needsFix(card)) forceVisible(card);
        });
      }, 2500);
    };

    const tryMount = () => {
      const whatEl = document.getElementById('works-landing-what-we-build');
      const showcaseEl = document.getElementById('works-landing-showcase');
      if (!whatEl || !showcaseEl) return false;
      mountLandingWorksSplit(whatEl, showcaseEl);
      runHeroFailsafe(whatEl);
      return true;
    };

    if (!tryMount()) {
      requestAnimationFrame(() => {
        if (!tryMount()) setTimeout(tryMount, 120);
      });
    }
  }, []);

  useEffect(() => {
    const showcaseEl = document.getElementById('works-landing-showcase');
    if (!showcaseEl) return;
    const sections = Array.from(showcaseEl.querySelectorAll<HTMLElement>('.works-usecase-section'));
    if (sections.length === 0) return;

    const seededShuffle = <T,>(arr: T[], seedStr: string): T[] => {
      let seed = 0;
      for (let i = 0; i < seedStr.length; i++) seed = (seed * 31 + seedStr.charCodeAt(i)) >>> 0;
      const next = () => {
        seed = (seed * 1664525 + 1013904223) >>> 0;
        return seed / 4294967296;
      };
      const out = arr.slice();
      for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(next() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
      }
      return out;
    };

    sections.forEach((section, sIdx) => {
      if (section.dataset.galleryMounted === 'true') return;

      const header = section.querySelector<HTMLElement>('.works-usecase-header');
      const grid = section.querySelector<HTMLElement>('.works-usecase-grid');
      if (!header || !grid) return;

      const catTitle = header.querySelector<HTMLElement>('.works-usecase-title')?.textContent?.trim() || 'Case Studies';
      const catIntro = header.querySelector<HTMLElement>('.works-usecase-intro')?.textContent?.trim() || '';
      const catId = section.getAttribute('data-category') || `${sIdx}`;

      const cards = Array.from(grid.querySelectorAll<HTMLElement>('.expandable-card-trigger[data-project-idx]'));
      if (cards.length === 0) return;

      const originalByIdx = new Map<string, HTMLElement>();
      cards.forEach((el) => {
        const key = el.getAttribute('data-project-idx');
        if (key) originalByIdx.set(key, el);
      });

      const itemsRaw: Gallery4Item[] = cards
        .map((card, idx) => {
          const title = card.querySelector<HTMLElement>('.expandable-card-title')?.textContent?.trim() || `Project ${idx + 1}`;
          const desc = card.querySelector<HTMLElement>('.expandable-card-desc')?.textContent?.trim() || '';
          const img = card.querySelector<HTMLImageElement>('img');
          const image = img?.getAttribute('data-src') || img?.getAttribute('src') || '';
          const projectIdxJson = card.getAttribute('data-project-idx') || '';
          if (!image || !projectIdxJson) return null;
          return { id: `case-${sIdx}-${idx}`, title, description: desc, image, projectIdxJson };
        })
        .filter(Boolean) as Gallery4Item[];

      const items = seededShuffle(itemsRaw, `${catId}-${catTitle}`);
      if (items.length < 2) return;

      const mount = document.createElement('div');
      mount.className = 'landing-gallery4-mount';
      header.insertAdjacentElement('afterend', mount);

      createRoot(mount).render(
        <Gallery4
          title={catTitle}
          description={catIntro}
          items={items}
          showHeader={false}
          autoPlayIntervalMs={1500}
          onItemClick={(item) => {
            originalByIdx.get(item.projectIdxJson)?.click();
          }}
        />,
      );

      // Hide the original grid only after the carousel mounts. This prevents "empty sections"
      // on some mobile browsers if React fails to mount for any reason.
      const hideOriginalGrid = () => {
        const mounted = mount.childElementCount > 0;
        if (mounted) {
          grid.style.display = 'none';
        }
      };
      requestAnimationFrame(hideOriginalGrid);
      setTimeout(hideOriginalGrid, 500);

      section.dataset.galleryMounted = 'true';
    });

    revealShowcaseContent(showcaseEl);
    requestAnimationFrame(() => revealShowcaseContent(showcaseEl));
    setTimeout(() => revealShowcaseContent(showcaseEl), 600);
  }, []);

  return (
    <>
      <LandingNav />
      <main id="main-content">
        <CinematicHero
          openingTitle="Systems. Platforms. Digital Infrastructure."
          openingBody={
            <>
              ThinkinCode builds scalable digital systems that improve operations, increase efficiency, and drive
              measurable business outcomes. We transform complex business challenges into elegant, high-performance
              solutions that scale with your growth.
            </>
          }
          cardHeading="Where ideas meet code."
          cardDescription={
            <>
              We don&apos;t just ship software. We craft calm, precise experiences and systems built to scale with
              your business.
            </>
          }
          metricValue={100}
          metricLabel="Projects delivered"
        />

        <section id="works" className="works-section" data-scroll-section>
          <div className="works-container">
            <div id="works-landing-showcase" />
          </div>
        </section>

        <section className="works-cta-strip">
          <p className="works-cta-strip-text">
            Have an idea? <strong>Let&apos;s make it happen.</strong>
          </p>
          <a href="/#contact" className="works-cta-strip-btn">
            Get in touch
          </a>
        </section>

        <LandingFooter />
      </main>
    </>
  );
}

createRoot(rootEl).render(<LandingApp />);
